/** Dropdown/tooltip portal outside #ui-scale-root. */
export const UI_POPUP_ROOT_ID = 'ui-popup-root'

export function getUiScale(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-ui-scale')
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

export interface UiScaleBreakpoints {
  baseWidth: number
  baseScale: number
  wideWidth: number
  wideScale: number
}

const DEFAULT_BREAKPOINTS: UiScaleBreakpoints = {
  baseWidth: 1920,
  baseScale: 1,
  wideWidth: 2560,
  wideScale: 1,
}

const MIN_SCALE = 0.5
const MAX_SCALE = 1.5

function parseOptionalFloat(value: string | undefined): number | undefined {
  if (value == null || value.trim() === '') return undefined
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))
}

/** Per-client overrides from theme CSS (`--app-ui-scale-base` / `--app-ui-scale-wide`). */
export function readThemeUiScaleBreakpoints(
  breakpoints: UiScaleBreakpoints = DEFAULT_BREAKPOINTS,
): UiScaleBreakpoints {
  if (typeof document === 'undefined') return breakpoints

  const styles = getComputedStyle(document.documentElement)
  const baseScale = parseOptionalFloat(styles.getPropertyValue('--app-ui-scale-base'))
  const wideScale = parseOptionalFloat(styles.getPropertyValue('--app-ui-scale-wide'))
  const baseWidth = parseOptionalFloat(styles.getPropertyValue('--app-ui-scale-base-width'))
  const wideWidth = parseOptionalFloat(styles.getPropertyValue('--app-ui-scale-wide-width'))

  return {
    baseWidth: baseWidth ?? breakpoints.baseWidth,
    baseScale: baseScale ?? breakpoints.baseScale,
    wideWidth: wideWidth ?? breakpoints.wideWidth,
    wideScale: wideScale ?? breakpoints.wideScale,
  }
}

export function computeResponsiveUiScale(
  viewportWidth: number,
  breakpoints: UiScaleBreakpoints,
): number {
  const { baseWidth, baseScale, wideWidth, wideScale } = breakpoints

  if (viewportWidth <= baseWidth) return baseScale
  if (viewportWidth >= wideWidth) return wideScale

  const progress = (viewportWidth - baseWidth) / (wideWidth - baseWidth)
  return baseScale + progress * (wideScale - baseScale)
}

export function resolveUiScale(
  viewportWidth: number,
  breakpoints: UiScaleBreakpoints = readThemeUiScaleBreakpoints(),
): number {
  return clampScale(computeResponsiveUiScale(viewportWidth, breakpoints))
}

export function applyUiScale(scale: number): void {
  const root = document.documentElement
  const normalized = clampScale(scale)

  root.style.setProperty('--app-ui-scale', String(normalized))
  root.style.setProperty('--app-ui-scale-inverse', String(1 / normalized))

  if (normalized === 1) {
    delete root.dataset.uiScaleApplied
  } else {
    root.dataset.uiScaleApplied = 'true'
  }
}

export function initUiScale(): () => void {
  const update = () => {
    const breakpoints = readThemeUiScaleBreakpoints()
    const scale = resolveUiScale(window.innerWidth, breakpoints)
    applyUiScale(scale)
  }

  update()

  let frame = 0
  const onResize = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(update)
  }

  window.addEventListener('resize', onResize)
  return () => {
    cancelAnimationFrame(frame)
    window.removeEventListener('resize', onResize)
  }
}
